import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useBoolean } from 'usehooks-ts';

import { removeEmploymentDetails } from '@/api/user';

import { Icon } from '@/components/Icon/Icon';
import { ConfirmDrawer } from '@/components/Layout/Drawer/ConfirmDrawer';

import { EMPLOYMENT_STATUS_TEXT, PROFESSION_TEXT } from '@/constants/user';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';
import { selectUserData } from '@/redux/user-data/selectors';

import {
  DocumentSection,
  DocumentType,
  Document as UserDocument,
} from '@/types/document';
import { EmploymentStatus, Profession, User } from '@/types/user';

import { formatDateValue } from '@/utils/string';
import { getOccupationName } from '@/utils/user';

import { ViewDocumentDrawer } from '../../../../drawers/ViewDocumentDrawer';

import {
  EmploymentInfoReviewDocumentIcon,
  EmploymentInfoReviewFieldLabel,
  EmploymentInfoReviewFieldValue,
  ReviewEmploymentInfoDetailCard,
  ReviewEmploymentInfoDrawerContainer,
  ReviewEmploymentInfoDrawerHeader,
  StyledReviewEmploymentInfoDrawer,
  UploadedDocumentItem,
} from './ReviewPersonalInfoDrawer.styled';

type ReviewEmploymentInfoDrawerProps = {
  isProofOfEmploymentApproved: boolean;
  isReviewEmploymentDrawerOpen: boolean;
  onClose: () => void;
};

export function ReviewEmploymentInfoDrawer({
  isProofOfEmploymentApproved,
  isReviewEmploymentDrawerOpen,
  onClose,
}: Readonly<ReviewEmploymentInfoDrawerProps>) {
  const { t } = useTranslate();
  const { user } = useAppSelector(selectUserData);
  const { occupations, directorOccupations } =
    useAppSelector(selectConfigsState);
  // user availability is already checked from parent MoreInformationForm component
  const userInfo = user as User;
  const { proofOfEmploymentDescriptionByType, dateFormat } =
    useAppSelector(selectConfigsState);
  const {
    value: showConfirmDrawer,
    setTrue: openConfirmDrawer,
    setFalse: closeConfirmDrawer,
  } = useBoolean(false);

  const [docToView, setDocToView] = useState<UserDocument>();

  const documents = userInfo.documents.filter(
    item => item.type === DocumentSection.ProofOfEmployment,
  );

  const { mutate, isPending, isSuccess } = useMutationWithUserData({
    mutationFn: removeEmploymentDetails,
  });

  useEffect(() => {
    if (isSuccess) {
      closeConfirmDrawer();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const onCloseDocumentViewer = () => setDocToView(undefined);

  const onDocumentClick = (userDocument: UserDocument) => {
    setDocToView({
      ...userDocument,
      documentURI: userDocument.uri || '',
    } as UserDocument);
  };

  return (
    <StyledReviewEmploymentInfoDrawer
      open={isReviewEmploymentDrawerOpen}
      onClose={onClose}
      hideBackdrop={false}
    >
      <ReviewEmploymentInfoDrawerHeader>
        <Icon name={'close'} size={18} onClick={onClose} cursor={'pointer'} />
        <Typography flex={1} textAlign={'center'}>
          {t(
            'moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.employmentDetails',
          )}
        </Typography>
      </ReviewEmploymentInfoDrawerHeader>
      <ReviewEmploymentInfoDrawerContainer>
        <Typography variant="h3" textAlign={'center'}>
          {t(
            'moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.employmentDetails',
          )}
        </Typography>
        <ReviewEmploymentInfoDetailCard>
          <EmploymentInfoReviewFieldLabel>
            {t(
              'moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.employmentStatus',
            )}
          </EmploymentInfoReviewFieldLabel>

          <EmploymentInfoReviewFieldValue>
            {userInfo.employmentDetailed?.employmentStatus
              ? t(
                  EMPLOYMENT_STATUS_TEXT[
                    userInfo.employmentDetailed.employmentStatus
                  ],
                )
              : ''}
          </EmploymentInfoReviewFieldValue>
          {userInfo.employmentDetailed?.employmentStatus ===
            EmploymentStatus.Employed && (
            <>
              <EmploymentInfoReviewFieldLabel>
                {t(
                  'moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.occupation',
                )}
              </EmploymentInfoReviewFieldLabel>
              <EmploymentInfoReviewFieldValue>
                {getOccupationName({
                  user,
                  occupations,
                  directorOccupations,
                })}
              </EmploymentInfoReviewFieldValue>
              <EmploymentInfoReviewFieldLabel>
                {t(
                  'moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.employer',
                )}
              </EmploymentInfoReviewFieldLabel>
              <EmploymentInfoReviewFieldValue>
                {userInfo.employmentDetailed?.employerName}
              </EmploymentInfoReviewFieldValue>
              <EmploymentInfoReviewFieldLabel>
                {t(
                  'moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.since',
                )}
              </EmploymentInfoReviewFieldLabel>
              <EmploymentInfoReviewFieldValue>
                {formatDateValue(
                  userInfo.employmentDetailed?.startDate || '',
                  dateFormat,
                )}
              </EmploymentInfoReviewFieldValue>
              <EmploymentInfoReviewFieldLabel>
                {t(
                  'moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.phoneNumber',
                )}
              </EmploymentInfoReviewFieldLabel>
              <EmploymentInfoReviewFieldValue>
                {userInfo.employmentDetailed?.employerPhone}
              </EmploymentInfoReviewFieldValue>
              <EmploymentInfoReviewFieldLabel>
                {t(
                  'moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.mailingAddress',
                )}
              </EmploymentInfoReviewFieldLabel>
              <EmploymentInfoReviewFieldValue>
                {userInfo.employmentDetailed?.employerAddress}
              </EmploymentInfoReviewFieldValue>
            </>
          )}

          {(userInfo.employmentDetailed?.employmentStatus ===
            EmploymentStatus.Unemployed ||
            userInfo.employmentDetailed?.employmentStatus ===
              EmploymentStatus.SelfEmployed) && (
            <>
              <EmploymentInfoReviewFieldLabel>
                {t(
                  'moreInfo.reviewPersonalInfo.drawer.reviewEmploymentInfo.unemploymentType',
                )}
              </EmploymentInfoReviewFieldLabel>
              <EmploymentInfoReviewFieldValue>
                {userInfo.employmentDetailed.employmentStatus ===
                EmploymentStatus.Unemployed
                  ? userInfo.employmentDetailed.employmentDesc
                  : t(
                      PROFESSION_TEXT[
                        userInfo.employmentDetailed.employmentDesc as Profession
                      ],
                    )}
              </EmploymentInfoReviewFieldValue>
            </>
          )}
        </ReviewEmploymentInfoDetailCard>
        <Typography variant="h3" textAlign={'center'}>
          {t(
            'moreInfo.reviewPersonalInfo.drawer.reviewEmploymentInfo.uploadedDocuments',
          )}
        </Typography>
        {documents.map((item, index) => (
          <UploadedDocumentItem key={index}>
            <EmploymentInfoReviewDocumentIcon name="buildings" size={80} />
            <Box flex={1}>
              <Typography variant="body2Medium">
                {item.docType && t(`documentType.${item.docType}`)}
              </Typography>
              <Typography variant="caption" mt={0.5}>
                {item.docType &&
                  t(
                    proofOfEmploymentDescriptionByType[
                      item.docType as DocumentType
                    ]?.description || '',
                  )}
              </Typography>
            </Box>
            <Icon name="arrow-right" onClick={() => onDocumentClick(item)} />
          </UploadedDocumentItem>
        ))}
        {!isProofOfEmploymentApproved && (
          <Button
            variant="outlined"
            onClick={openConfirmDrawer}
            disabled={isPending}
          >
            {t(
              'moreInfo.reviewPersonalInfo.drawer.reviewEmploymentInfo.changeInformation',
            )}
          </Button>
        )}
      </ReviewEmploymentInfoDrawerContainer>
      {showConfirmDrawer && (
        <ConfirmDrawer
          title={t(
            'moreInfo.reviewPersonalInfo.drawer.reviewEmploymentInfo.changeEmploymentStatus',
          )}
          description={t(
            'moreInfo.reviewPersonalInfo.drawer.reviewEmploymentInfo.changeEmploymentDescription',
          )}
          secondaryAction={closeConfirmDrawer}
          secondaryText={t('common.cancel')}
          primaryAction={mutate}
          primaryText={t('common.confirm')}
          onClose={closeConfirmDrawer}
        />
      )}
      <ViewDocumentDrawer
        open={!!docToView}
        section={{
          docs: [],
          type: DocumentSection.ProofOfEmployment,
        }}
        document={docToView}
        onClose={onCloseDocumentViewer}
        title={docToView ? t(`documentType.${docToView.docType}`) : ''}
      />
    </StyledReviewEmploymentInfoDrawer>
  );
}
